import { axiosPrivate } from "../../../axios-config/axiosPrivate";
import { BACKEND } from "../../../config/backend";

const ARTIST_BASE_URL = `${BACKEND}/api/v1/artists`;

/**
 * Get artist detail data with specific segments
 * @param {Object} params
 * @param {number} params.artistId - The artist ID
 * @param {string} params.segments - Comma-separated list of segments (e.g., 'mail', 'base', 'rechte')
 * @returns {Promise<Object>} Artist detail data
 */
export const getArtistDetailData = async ({ artistId, segments }) => {
    const response = await axiosPrivate.get(`${ARTIST_BASE_URL}/${artistId}`, {
        params: { segments }
    });
    return response.data;
};

/**
 * Set the studio mail address an artist sends through; admins only.
 * @param {Object} params
 * @param {number} params.ArtistID - The artist ID
 * @param {number} params.MailAddressID - The studio mail address to send through
 * @returns {Promise<Object>} Response body
 */
export const updateArtistSenderMail = async ({ ArtistID, MailAddressID }) => {
    const response = await axiosPrivate.put(`${ARTIST_BASE_URL}/${ArtistID}/sender-mail`, { MailAddressID });
    return response.data;
};

/** The personal address of one artist with its state and where login mails and notices go. */
export const getArtistPrivateMail = async (artistId) =>
    (await axiosPrivate.get(`${ARTIST_BASE_URL}/${artistId}/private-mail`)).data;

/** The personal addresses of all artists that have an entry; admins only. */
export const getArtistPrivateMails = async () => (await axiosPrivate.get(`${ARTIST_BASE_URL}/private-mails`)).data;

/** Requests a new personal address; a confirmation link goes to it. `Passwort` is needed for non-admins. */
export const requestArtistPrivateMail = async ({ ArtistID, ...body }) =>
    (await axiosPrivate.put(`${ARTIST_BASE_URL}/${ArtistID}/private-mail`, body)).data;

/** Sends a new confirmation link for the open request; the old link becomes invalid. */
export const resendArtistPrivateMailLink = async ({ ArtistID }) =>
    (await axiosPrivate.post(`${ARTIST_BASE_URL}/${ArtistID}/private-mail/resend`)).data;

export const cancelArtistPrivateMailRequest = async ({ ArtistID }) =>
    (await axiosPrivate.delete(`${ARTIST_BASE_URL}/${ArtistID}/private-mail/request`)).data;

/** Removes the confirmed personal address. `Passwort` is needed for non-admins. */
export const removeArtistPrivateMail = async ({ ArtistID, ...body }) =>
    (await axiosPrivate.post(`${ARTIST_BASE_URL}/${ArtistID}/private-mail/remove`, body)).data;

/**
 * Save artist rights data.
 * @param {Object} artistRechteData - Rights payload
 * @param {number} artistRechteData.ArtistID - Artist ID
 * @param {Object} [artistRechteData.rechteBase] - Optional base rights segment payload
 * @param {Array<Object>} [artistRechteData.rechte] - Optional rights list payload
 * @returns {Promise<Object>} Save response
 */
export const saveArtistRechte = async (artistRechteData) => {
    const response = await axiosPrivate.put(
        `${ARTIST_BASE_URL}/${artistRechteData.ArtistID}/rechte`,
        artistRechteData
    );
    return response.data;
};

